// The top bar's "Load a shared item" dropdown: paste an `sb1:` string and load
// the scene it holds into the editor. It hangs off the bar rather than living on
// one route, so a string can be loaded from wherever the user is.

import { decodeShare } from "./share.js";

export type ImportElements = {
  toggle: HTMLButtonElement;
  panel: HTMLElement;
  input: HTMLInputElement;
  loadButton: HTMLButtonElement;
  errorEl: HTMLElement;
};

// `loadScene` restores a scene JSON and shows the editor, returning a message
// when it can't be loaded.
export function wireImportPanel(
  els: ImportElements,
  loadScene: (sceneJson: string) => string | null,
) {
  // A load in flight; a repeated Enter would otherwise decode and rebuild twice.
  let loading = false;

  function setOpen(open: boolean) {
    els.panel.hidden = !open;
    els.toggle.setAttribute("aria-expanded", String(open));
    // A message is about the string that raised it, so it doesn't outlive the
    // panel it was shown in.
    els.errorEl.textContent = "";
    // Focus without scrolling: the page reserves the docked viewer's height
    // above a focused control, which here would jump the page down instead.
    if (open) els.input.focus({ preventScroll: true });
  }

  // Dismissing by keyboard or by loading takes the focused input away with the
  // panel, so focus goes back to what opened it.
  function close() {
    setOpen(false);
    els.toggle.focus({ preventScroll: true });
  }

  // Anything wrong with a pasted string is reported next to the box, where the
  // user is looking, and leaves it alone so it can be corrected.
  async function load() {
    if (loading) return;
    loading = true;
    els.errorEl.textContent = "";
    try {
      let sceneJson: string;
      try {
        sceneJson = await decodeShare(els.input.value);
      } catch (err) {
        els.errorEl.textContent = (err as Error).message;
        return;
      }
      const error = loadScene(sceneJson);
      if (error) {
        els.errorEl.textContent = error;
        return;
      }
      // The scene is loaded and the editor is showing it; nothing left to paste.
      els.input.value = "";
      close();
    } finally {
      loading = false;
    }
  }

  els.toggle.addEventListener("click", () => setOpen(els.panel.hidden));
  els.loadButton.addEventListener("click", () => load());
  // The panel is not in a form, so Enter has nothing to submit otherwise.
  els.input.addEventListener("keydown", (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    load();
  });

  document.addEventListener("keydown", (e) => {
    if (e.key !== "Escape" || els.panel.hidden) return;
    close();
  });

  // A press anywhere else dismisses the panel. The toggle is excluded so its
  // own press doesn't close the panel its click is about to reopen.
  document.addEventListener("pointerdown", (e) => {
    const target = e.target as Node;
    if (els.panel.hidden) return;
    if (els.panel.contains(target) || els.toggle.contains(target)) return;
    setOpen(false);
  });
}
